
import { Component, Inject, Prop, Vue } from 'vue-property-decorator';

import { HttpClient, Project } from 'ag-client-typescript';
import * as FileSaver from 'file-saver';

import { GlobalData } from '@/app.vue';
import APIErrors from '@/components/api_errors.vue';
import ProgressOverlay from '@/components/progress_overlay.vue';
import { handle_api_errors_async, handle_global_errors_async, make_error_handler_func } from '@/error_handling';
import { BeforeDestroy, Created } from '@/lifecycle';
import { Poller } from '@/poller';
import { format_datetime_short, sleep, toggle, zip } from '@/utils';

export enum DownloadType {
  all_scores = 'all_scores',
  final_graded_submission_scores = 'final_graded_submission_scores',
  all_submission_files = 'all_submission_files',
  final_graded_submission_files = 'final_graded_submission_files'
}

// The API for DownloadTask has significant design issues, and it will likely
// be rewritten or replaced in the future.
export interface DownloadTask {
  pk: number;
  project: number;
  download_type: DownloadType;
  result_filename: string;
  progress: number;
  error_msg: string;
  created_at: string;
}

@Component({
  components: {
    APIErrors,
    ProgressOverlay,
  }
})
export default class DownloadGrades extends Vue implements Created, BeforeDestroy {
  @Inject({from: 'globals'})
  globals!: GlobalData;
  d_globals = this.globals;

  @Prop({required: true, type: Project})
  project!: Project;

  d_loading = true;
  d_downloads: DownloadTask[] = [];

  d_download_grades = true;
  d_include_staff = false;
  d_include_pending_extensions = false;
  d_final_graded_submissions_only = true;
  d_creating_download_task = false;

  d_downloading_result = false;
  d_downloading_result_progress: number | null = null;

  downloads_poller: Poller | null = null;

  readonly format_datetime_short = format_datetime_short;

  @handle_global_errors_async
  async created() {
    await this.load_download_tasks();
    this.d_loading = false;
    this.downloads_poller = new Poller(() => this.load_download_tasks(), 15);
    // tslint:disable-next-line no-floating-promises
    this.downloads_poller.start_after_delay();
  }

  async load_download_tasks() {
    let response = await HttpClient.get_instance().get<DownloadTask[]>(
      `/projects/${this.project.pk}/download_tasks/`);
    this.d_downloads = response.data.sort((first, second) => second.pk - first.pk);
  }

  beforeDestroy() {
    if (this.downloads_poller !== null) {
      this.downloads_poller.stop();
    }
  }

  @handle_api_errors_async(make_error_handler_func())
  create_download_task() {
    return toggle(this, 'd_creating_download_task', async () => {
      let download_type: DownloadType;
      if (this.d_download_grades) {
        download_type
          = this.d_final_graded_submissions_only
            ? DownloadType.final_graded_submission_scores : DownloadType.all_scores;
      }
      else {
        download_type
          = this.d_final_graded_submissions_only
            ? DownloadType.final_graded_submission_files : DownloadType.all_submission_files;
      }

      let response = await HttpClient.get_instance().post<DownloadTask>(
        this.get_create_task_url(download_type), {download_type: download_type});

      this.d_downloads.unshift(response.data);
    });
  }

  get_create_task_url(download_type: DownloadType) {
    let base_url = `/projects/${this.project.pk}/`;
    if (download_type === DownloadType.all_scores) {
      base_url += 'all_submission_scores/';
    }
    else if (download_type === DownloadType.final_graded_submission_scores) {
      base_url += 'ultimate_submission_scores/';
    }
    else if (download_type === DownloadType.all_submission_files) {
      base_url += 'all_submission_files/';
    }
    else if (download_type === DownloadType.final_graded_submission_files) {
      base_url += 'ultimate_submission_files/';
    }

    let query_options: {[param_name: string]: boolean} = {};
    if (this.d_include_staff) {
      query_options['include_staff'] = true;
    }
    if (this.d_include_pending_extensions) {
      query_options['include_pending_extensions'] = true;
    }
    if (Object.keys(query_options).length !== 0) {
      base_url += '?';
      base_url += Object.entries(query_options).filter(
        entry => entry[1]
      ).map(entry => `${entry[0]}=true`).join('&');
    }

    return base_url;
  }

  get_filename(download_task: DownloadTask) {
    let file_suffix = null;
    if (download_task.download_type === DownloadType.all_scores) {
        file_suffix = '_all_scores.csv';
    }
    else if (download_task.download_type === DownloadType.final_graded_submission_scores) {
        file_suffix = '_final_graded_submission_scores.csv';
    }
    else if (download_task.download_type === DownloadType.all_submission_files) {
        file_suffix = '_all_submission_files.zip';
    }
    else if (download_task.download_type === DownloadType.final_graded_submission_files) {
        file_suffix = '_final_graded_submission_files.zip';
    }
    return this.project.name + file_suffix;
  }

  @handle_global_errors_async
  download_task_result(download_task: DownloadTask) {
    this.d_downloading_result_progress = null;
    return toggle(this, 'd_downloading_result', async () => {
      let response = await HttpClient.get_instance().get_file(
        `/download_tasks/${download_task.pk}/result/`,
        {on_download_progress:
          (event: ProgressEvent) => {
            if (event.lengthComputable) {
              this.d_downloading_result_progress = 100 * (1.0 * event.loaded / event.total);
            }
          }
        }
      );
      FileSaver.saveAs(new File([response.data], this.get_filename(download_task)));
      this.d_downloading_result_progress = null;
    });
  }
}

